import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
/* eslint-disable consistent-return */

import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import { theme, whenActive, whenFocused, whenHovered } from "../../theme";
import { easeCurves } from "../../motion/eases";
import { ToggleContext } from "./ToggleContext";
import { Transition } from 'react-transition-group';
import { reflow } from "../../utils/reflow";
var ANIMATION_DURATION = 125;
/**
 * A Checkbox widget to optionally be used within a `Toggle`.
 * If used outside of `Toggle` you'll need to ensure you provide
 * the correct accessibility labels yourself.
 */

export var ToggleCheckbox = React.memo(React.forwardRef(function (_ref, forwardedRef) {
  var indeterminate = _ref.indeterminate,
      other = _objectWithoutProperties(_ref, ["indeterminate"]);

  var context = useContext(ToggleContext); // To enable ToggleCheckbox to potentially be used outside of the Toggle
  // context provider, we fallback to allowing props to manage things
  // like checked, focused, disabled, etc.

  var _ref2 = context !== null && context !== void 0 ? context : other,
      tapped = _ref2.tapped,
      checked = _ref2.checked,
      disabled = _ref2.disabled,
      hovering = _ref2.hovering,
      focused = _ref2.focused;

  return /*#__PURE__*/React.createElement(StyledCheckbox, _extends({
    disabled: disabled,
    $hovering: hovering,
    $tapped: tapped,
    $focused: focused,
    ref: forwardedRef
  }, other), /*#__PURE__*/React.createElement(Transition, {
    in: checked || indeterminate,
    mountOnEnter: true,
    unmountOnExit: true,
    timeout: ANIMATION_DURATION,
    onEnter: function onEnter(el) {
      return reflow(el);
    }
  }, function (state) {
    return /*#__PURE__*/React.createElement(Contents, {
      "data-disabled": disabled,
      "data-indeterminate": indeterminate,
      "data-checked": checked,
      "data-state": state
    });
  }));
}));
var BORDER_RADIUS = theme.radii[4];
var fill = css(["position:absolute;top:0;left:0;right:0;bottom:0;"]);
var background = css(["", ";opacity:0;background-color:", ";border-radius:", ";transition-property:opacity;transition-duration:", "ms;transition-timing-function:", ";&[data-disabled]{background-color:", ";}&[data-state='entering'],&[data-state='entered']{opacity:1;}&[data-state='exiting']{opacity:0;}"], fill, theme.colors.palette.brandPrimary, BORDER_RADIUS, ANIMATION_DURATION, easeCurves.primary, theme.colors.palette.tintA10);
var check = css([":after{", ";content:'';background-position:50%;background-repeat:no-repeat;background-image:url(\"data:image/svg+xml, %3Csvg width='10' height='9' viewBox='0 0 10 9' fill='none' xmlns='http://www.w3.org/2000/svg' %3E%3Cpath d='M4.12158 8.82129C4.4292 8.82129 4.6709 8.70264 4.83789 8.45654L8.9292 2.2207C9.04785 2.04053 9.09619 1.86914 9.09619 1.71094C9.09619 1.28467 8.771 0.968262 8.33154 0.968262C8.03271 0.968262 7.84375 1.07812 7.65918 1.36377L4.104 6.97119L2.30225 4.76074C2.13525 4.55859 1.95068 4.4707 1.6958 4.4707C1.25195 4.4707 0.931152 4.78711 0.931152 5.21777C0.931152 5.41113 0.988281 5.57812 1.15527 5.76709L3.42285 8.4917C3.61182 8.71582 3.83154 8.82129 4.12158 8.82129Z' fill='white' /%3E%3C/svg%3E\");transition-property:transform,opacity;transition-duration:", "ms;transition-timing-function:", ";transform:scale(0);opacity:0;}&[data-checked='true'][data-state='entering']:after,&[data-checked='true'][data-state='entered']:after{opacity:1;transform:scale(0.9999999);}&[data-state='exiting']:after{opacity:0;transform:scale(0);}&[data-indeterminate='true']:after{visibility:hidden;}&[data-disabled][data-checked='true']:after{opacity:0.3;}"], fill, ANIMATION_DURATION, easeCurves.primary);
var indeterminate = css([":before{position:absolute;top:50%;left:50%;content:'';width:", ";height:", ";border-radius:", ";background:white;transition-property:transform,opacity;transition-duration:", "ms;transition-timing-function:", ";transform:translate(-50%,-50%) scale(0);opacity:0;}&[data-disabled]:before{background:", ";}&[data-checked='true']:not([data-indeterminate='true']):before{visibility:hidden;}&[data-indeterminate='true'][data-state='entering']:before,&[data-indeterminate='true'][data-state='entered']:before{opacity:1;transform:translate(-50%,-50%) scale(1);}&[data-state='exiting']:before{opacity:0;transform:translate(-50%,-50%) scale(0);}"], theme.spaces[8], theme.spaces[2], theme.spaces[8], ANIMATION_DURATION, easeCurves.primary, theme.colors.palette.tintA30);
var Contents = styled.div.withConfig({
  displayName: "ToggleCheckbox__Contents",
  componentId: "vapor__sc-1pxhjup-0"
})(["", ";", ";", ";", ";"], fill, background, indeterminate, check);
var hovered = css(["box-shadow:inset 0 0 0 1px ", ";"], theme.colors.palette.whiteA40);
var active = css(["box-shadow:inset 0 0 0 2px ", ";"], theme.colors.palette.whiteA40);
var StyledCheckbox = styled.div.withConfig({
  displayName: "ToggleCheckbox__StyledCheckbox",
  componentId: "vapor__sc-1pxhjup-1"
})(["box-sizing:border-box;width:", ";height:", ";border-radius:", ";position:relative;transition:box-shadow 0.2s ", ";box-shadow:inset 0 0 0 1px ", ";", "{box-shadow:0 0 0 1px ", ";}", " ", ":not([disabled]){", ";}", ":not([disabled]){", ";}", ";"], theme.spaces[16], theme.spaces[16], BORDER_RADIUS, easeCurves.primary, theme.colors.palette.whiteA30, whenFocused, theme.colors.palette.whiteA70, function (p) {
  return p.$focused && "\n      box-shadow: 0 0 0 1px ".concat(theme.colors.palette.whiteA70, ";\n  ");
}, whenHovered, hovered, whenActive, active, function (p) {
  if (p.disabled) {
    return css(["box-shadow:none;background-color:", ";"], theme.colors.palette.tintA10);
  }

  if (p.$tapped) {
    return active;
  }

  if (p.$hovering) {
    return hovered;
  }
});